<!-- https://vue3datepicker.com/props/calendar-configuration/ -->
<template>
  <div>
    <!-- Input natif pour mobile iOS et Android -->
     <div v-if="isMobileDevice">
      <label v-if="props.label" :for="'dp-input-datePicker-' + $.uid">{{ props.label }} {{ props.required ? '*' : '' }}</label>

      <input
        :id="'dp-input-datePicker-' + $.uid"
        type="date"
        v-model="nativeValue"
        :required="props.required"
        :min="nativeMinDate"
        :max="nativeMaxDate"
        :placeholder="props.placeholder"
        class="form-control"
        :class="[errorMessage ? 'is-invalid' : '']"
        @change="emit('change')"
        :disabled="props.disabled"
      />
    </div>
    <!-- VueDatePicker pour desktop -->
     <div v-else class="form-floating" style="border-color: #86868b;">
      <VueDatePicker
        v-model="value"
        :input-attrs="{
          id: 'dp-input-datePicker-' + $.uid,
          clearable: !props.required,
          required: props.required
        }"
        :auto-apply="props.autoApply"
        :time-config="{
          enableTimePicker: props.enableTimePicker || props.onlyTimePicker,
          minutesGridIncrement: props.minutesIncrement,
          startTime: { hours: 10, minutes: 0, seconds: 0 }
        }"
        :model-type="props.onlyTimePicker ? 'HH:mm:ss' : undefined"
        :no-today="props.noToday"
        :placeholder="props.placeholder"
        :locale="fr"
        :formats="{
          month: props.monthNameFormat,
          input: props.format
        }"
        :flow="{ steps: flowComputed, partial: true }"
        :min-date="props.minDate"
        :max-date="props.maxDate"
        :prevent-min-max-navigation="preventMinMaxNavigation"
        :class="[errorMessage ? 'is-invalid' : '']"
        :ui="{input: [errorMessage ? 'is-invalid' : '']}"
        :year-range="yearRange"
        @update:model-value="emit('change')"
        class=""
        style=""
        :time-picker="props.onlyTimePicker"
        :config="{ keepActionRow: props.onlyTimePicker }"
        :disabled="props.disabled"
      />
      <label v-if="props.label" style="color: #86868b; transform: scale(0.85) translateY(-0.5rem) translateX(0.15rem);" :for="'dp-input-datePicker-' + $.uid">{{ props.label }} {{ props.required ? '*' : '' }}</label>
    </div>

    <div class="invalid-feedback">{{ errorMessage }}</div>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import { useField } from 'vee-validate';
import { fr } from 'date-fns/locale'


const props = withDefaults(defineProps<{
  valueKey: AllTypeKeys;
  label?: string;
  autoApply?: boolean;
  enableTimePicker?: boolean;
  onlyTimePicker?: boolean;
  noToday?: boolean;
  placeholder?: string;
  monthNameFormat?: string;
  format?: string;
  flow?: ('month' | 'year' | 'calendar' | 'time' | 'minutes' | 'hours' | 'seconds')[];
  required?: boolean;
  minDate?: Date | null;
  maxDate?: Date | null;
  minutesIncrement?: number;
  disabled?: boolean;
}>(), {
  autoApply: true,
  enableTimePicker: false,
  onlyTimePicker: false,
  noToday: false,
  placeholder: '',
  monthNameFormat: 'MMMM',
  format: 'dd/MM/yyyy',
  flow: () => ['year', 'month', 'calendar'],
  required: false,
  minDate: null,
  minutesIncrement: 1,
  disabled: false
})

const emit = defineEmits(['change'])

const flowComputed = computed(() => {
  let result = props.flow || [];
  if(props.minDate && props.maxDate && props.minDate.getFullYear() == props.maxDate.getFullYear()) {
    result = result.filter(step => step !== 'year');

    if(props.minDate.getMonth() == props.maxDate.getMonth()) {
      result = result.filter(step => step !== 'month');
    }
  }
  return result;
})

// Détection améliorée des appareils mobiles iOS et Android
const isMobileDevice = computed(() => {
  const userAgent = navigator.userAgent.toLowerCase();
  // Détection Android
  const isAndroid = /android/.test(userAgent);
  // Détection iOS (iPhone, iPad, iPod)
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  // Détection iPad moderne (iOS 13+) qui se présente comme un Mac
  // On vérifie si c'est un appareil tactile avec un user agent Mac
  const isIPadOS = /macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;
  // Vérification alternative avec navigator.platform (pour compatibilité)
  const platformIsIOS = /iphone|ipad|ipod/.test(navigator.platform?.toLowerCase() || '');
  const isMobile = isAndroid || isIOS || isIPadOS || platformIsIOS;
  return isMobile;
})

const preventMinMaxNavigation = computed(() => {
  return props.minDate !== null
})

const yearRange = computed(() => {
  const currentYear = new Date().getFullYear();
  return [
    props.minDate ? props.minDate.getFullYear() : currentYear - 100,
    props.maxDate ? props.maxDate.getFullYear() : currentYear + 10,
  ]
})

const { value, errorMessage } = useField(() => props.valueKey);

// Conversion pour l'input natif (format YYYY-MM-DD)
const formatDateToNative = (date: Date | null | undefined): string => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const parseDateFromNative = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  return new Date(dateStr);
}

const nativeValue = computed({
  get: () => formatDateToNative(value.value as Date),
  set: (val: string) => {
    value.value = parseDateFromNative(val);
  }
})

const nativeMinDate = computed(() => formatDateToNative(props.minDate))
const nativeMaxDate = computed(() => formatDateToNative(props.maxDate))
</script>
